import { z } from "zod";

// =============================================================================
// Animation Intermediate Representation
//
// The IR is the contract between the LLM, the template engine, and the
// editor UI. It is intentionally small and human-friendly so prompts like
// "make it bouncy and tech" map cleanly onto fields the engine understands.
// =============================================================================

export const Easing = z.enum([
  "linear",
  "easeInQuad", "easeOutQuad", "easeInOutQuad",
  "easeInCubic", "easeOutCubic", "easeInOutCubic",
  "easeInExpo", "easeOutExpo", "easeInOutExpo",
  "easeOutBack", "spring",
]);
export type Easing = z.infer<typeof Easing>;

export const Effect = z.enum([
  "glow", "particles", "shadow", "motion_blur",
  "chromatic_aberration", "trails", "sparks",
  "neon", "liquid", "glitch", "hologram", "scanlines",
]);
export type Effect = z.infer<typeof Effect>;

export const Mood = z.enum([
  "luxury", "tech", "gaming", "finance", "playful",
  "cinematic", "minimal", "retro", "editorial",
]);
export type Mood = z.infer<typeof Mood>;

export const TemplateId = z.enum([
  "stroke_reveal",
  "fade_in",
  "scale_pop",
  "slide_in",
  "morph",
  "glitch_in",
  "elastic_intro",
  "cinematic_zoom",
  "particle_assembly",
  "liquid_reveal",
  "kinetic_typography",
  "radial_reveal",
]);
export type TemplateId = z.infer<typeof TemplateId>;

// LLM model selection. Workers AI options are first-class — partner models
// route through AI Gateway when a token is configured.
export const ModelId = z.enum([
  "llama-3.3-70b",        // default — fast, JSON-mode capable
  "llama-3.1-8b",         // smaller, sometimes more reliable for short prompts
  "qwen-2.5-coder-32b",   // alternative reasoning style
  "mistral-7b",
  "deepseek-r1-distill",
]);
export type ModelId = z.infer<typeof ModelId>;

export const MODEL_INFO: Record<ModelId, { title: string; provider: string; cfModel: string; iconId: string; blurb: string }> = {
  "llama-3.3-70b":      { title: "Llama 3.3 70B",      provider: "Meta (Cloudflare AI)",        cfModel: "@cf/meta/llama-3.3-70b-instruct-fp8-fast", iconId: "metaai",   blurb: "Default. Fast JSON-mode + great template picking." },
  "llama-3.1-8b":       { title: "Llama 3.1 8B",       provider: "Meta (Cloudflare AI)",        cfModel: "@cf/meta/llama-3.1-8b-instruct",           iconId: "meta",     blurb: "Smaller model, faster cold-start." },
  "qwen-2.5-coder-32b": { title: "Qwen 2.5 Coder 32B", provider: "Alibaba (Cloudflare AI)",     cfModel: "@cf/qwen/qwen2.5-coder-32b-instruct",      iconId: "qwen",     blurb: "Different reasoning style; strong at JSON output." },
  "mistral-7b":         { title: "Mistral 7B",          provider: "Mistral (Cloudflare AI)",     cfModel: "@cf/mistral/mistral-7b-instruct-v0.2",     iconId: "mistral",  blurb: "Compact + dependable for short prompts." },
  "deepseek-r1-distill":{ title: "DeepSeek R1 Distill", provider: "DeepSeek (Cloudflare AI)",    cfModel: "@cf/deepseek-ai/deepseek-r1-distill-qwen-32b", iconId: "deepseek", blurb: "Reasoning-distilled, slower but thorough." },
};


const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const AnimationIR = z.object({
  templateId: TemplateId,
  // Real logo reveals live in the 800-2800ms band. Anything longer is the LLM
  // misreading the prompt — we cap hard to keep the feel cinema-tight.
  durationMs: z.number().int().min(700).max(3500),
  // Speed is a tiny perceptual nudge, not a 2.5x dial. The runtime clamps the
  // effective multiplier so a chatty LLM can't ruin the timing.
  speed: z.number().min(0.7).max(1.4).default(1),
  easing: Easing.default("easeOutExpo"),
  mood: Mood.default("minimal"),
  effects: z
    .array(z.object({ kind: Effect, intensity: z.number().min(0).max(1) }))
    .max(4)
    .default([]),
  colorOverrides: z
    .object({
      primary: HexColor.optional(),
      accent: HexColor.optional(),
      background: HexColor.optional(),
      transparent: z.boolean().optional(),
    })
    .default({ transparent: true }),
  strokeWidth: z.number().min(0).max(20).optional(),
  particleDensity: z.number().min(0).max(1).optional(),
  glowIntensity: z.number().min(0).max(1).optional(),
  perElement: z
    .record(
      z.string(),
      z.object({
        delayMs: z.number().min(0).optional(),
        animation: z.string().optional(),
      })
    )
    .optional(),
  // Which LLM produced this IR. Persisted for analytics + replay.
  model: ModelId.optional(),
});

export type AnimationIR = z.infer<typeof AnimationIR>;

// =============================================================================
// Compiled Timeline — what the runtime actually plays.
//
// `tracks[]` is a flat list of time-keyframed property animations against
// a CSS-selector or path id. Both the Remotion composition and the GSAP
// preview interpret this exact shape.
// =============================================================================

export const Keyframe = z.object({
  t: z.number().min(0).max(1),
  // Transform / paint properties — kept loose because per-template needs vary.
  opacity: z.number().optional(),
  scale: z.number().optional(),
  scaleX: z.number().optional(),
  scaleY: z.number().optional(),
  x: z.number().optional(),
  y: z.number().optional(),
  rotate: z.number().optional(),
  skewX: z.number().optional(),
  fillOpacity: z.number().optional(),
  strokeOpacity: z.number().optional(),
  strokeDasharray: z.number().optional(),
  strokeDashoffset: z.number().optional(),
  fill: z.string().optional(),
  stroke: z.string().optional(),
  filter: z.string().optional(),
});
export type Keyframe = z.infer<typeof Keyframe>;

export const Track = z.object({
  target: z.string(),                 // CSS selector or [data-id] match
  keyframes: z.array(Keyframe),
  easing: Easing.default("easeOutExpo"),
  delay: z.number().min(0).default(0),
});
export type Track = z.infer<typeof Track>;

export const Timeline = z.object({
  durationMs: z.number().int().min(400).max(15000),
  fps: z.number().int().min(12).max(60).default(30),
  width: z.number().int().min(64).max(4096).default(1080),
  height: z.number().int().min(64).max(4096).default(1080),
  background: z.string().default("transparent"),
  tracks: z.array(Track),
  // High-level effect descriptors — the runtime decides how to realise them
  // (CSS filter, post-render canvas pass, particle system, etc.).
  effects: z
    .array(z.object({ kind: Effect, intensity: z.number().min(0).max(1) }))
    .default([]),
});
export type Timeline = z.infer<typeof Timeline>;

// JSON schema used for LLM JSON Mode. Hand-rolled to avoid a runtime dep on
// zod-to-json-schema and to give us tight control over the prompt surface.
export const ANIMATION_IR_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["templateId", "durationMs"],
  properties: {
    templateId: { type: "string", enum: TemplateId.options },
    durationMs: { type: "integer", minimum: 700, maximum: 3500 },
    speed: { type: "number", minimum: 0.7, maximum: 1.4 },
    easing: { type: "string", enum: Easing.options },
    mood: { type: "string", enum: Mood.options },
    effects: {
      type: "array",
      maxItems: 4,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "intensity"],
        properties: {
          kind: { type: "string", enum: Effect.options },
          intensity: { type: "number", minimum: 0, maximum: 1 },
        },
      },
    },
    colorOverrides: {
      type: "object",
      additionalProperties: false,
      properties: {
        primary: { type: "string", pattern: "^#[0-9a-fA-F]{6}$" },
        accent: { type: "string", pattern: "^#[0-9a-fA-F]{6}$" },
        background: { type: "string", pattern: "^#[0-9a-fA-F]{6}$" },
        transparent: { type: "boolean" },
      },
    },
    strokeWidth: { type: "number", minimum: 0, maximum: 20 },
    particleDensity: { type: "number", minimum: 0, maximum: 1 },
    glowIntensity: { type: "number", minimum: 0, maximum: 1 },
    model: { type: "string", enum: ModelId.options },
  },
} as const;
